<script>
import { buildManifest } from '../js/buildManifest.js';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleExclamation, faSpinner } from '@fortawesome/free-solid-svg-icons';

library.add(faCircleExclamation, faSpinner);

export default {
  name: 'FirmwareUpdateModal',

  components: {
    FontAwesomeIcon
  },

  props: {
    show: {
      type: Boolean,
      required: true
    },
    connection: {
      type: Object,
      required: false,
      default: null
    },
    currentVersion: {
      type: String,
      required: false,
      default: null
    }
  },

  emits: ['close', 'complete', 'error'],

  data() {
    return {
      stage: null, // 'preparing', 'confirm', 'begin', 'ready', 'downloading', 'transferring', 'verifying', 'verified', 'committing', 'committed', 'rebooting', 'done', 'error'
      currentFile: null,
      currentFileIndex: 0,
      totalFiles: 0,
      errorMessage: null,
      isRetrying: false,
      manifest: null,
      preFlightProgress: 'idle', // 'fetching', 'downloading', 'verifying', 'complete', 'error'
      preFlightError: null,
    };
  },

  computed: {
    stageLabel() {
      const labels = {
        'begin': 'Starting firmware update...',
        'ready': 'Device ready for update',
        'downloading': 'Downloading firmware files...',
        'transferring': 'Transferring files to device',
        'verifying': 'Verifying firmware integrity',
        'verified': 'Firmware verified successfully',
        'committing': 'Committing firmware update',
        'committed': 'Update committed, rebooting device',
        'rebooting': 'Device rebooting...',
        'done': 'Update complete!',
        'error': 'Update failed'
      };
      return labels[this.stage] || 'Processing...';
    },

    isDismissable() {
      return ['confirm', 'error', 'done'].includes(this.stage);
    },

    progressPercent() {
      if (this.stage === 'downloading') {
        if (this.totalFiles === 0) return 0;
        return Math.round((this.currentFileIndex / this.totalFiles) * 100);
      }
      if (this.stage === 'transferring') {
        if (this.totalFiles === 0) return 0;
        return Math.round(((this.currentFileIndex - 1) / this.totalFiles) * 100);
      }
      return 0;
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.$nextTick(() => this.$refs.dialog?.showModal());
        this.prepareUpdate();
      } else {
        this.$refs.dialog?.close();
      }
    }
  },

  methods: {
    async prepareUpdate() {
      try {
        this.stage = 'preparing';
        this.errorMessage = null;
        this.preFlightError = null;
        this.manifest = null;

        // Pre-flight: fetch manifest and all firmware files into memory while the user reads release notes
        this.preFlightProgress = 'fetching';
        this.manifest = await buildManifest('latest', (progress) => {
          if (progress.status === 'downloading_file') {
            this.preFlightProgress = 'downloading';
            this.currentFile = progress.file;
            this.currentFileIndex = progress.current;
            this.totalFiles = progress.total;
          } else if (progress.status === 'ready') {
            this.preFlightProgress = 'verifying';
            this.totalFiles = progress.fileCount;
          } else if (progress.status === 'error') {
            this.preFlightProgress = 'error';
            this.preFlightError = progress.error;
          }
        }, this.currentVersion);

        this.preFlightProgress = 'complete';
        this.stage = 'confirm';
      } catch (err) {
        this.stage = 'error';
        this.errorMessage = err.message;
        this.$emit('error', { error: err.message });
      }
    },

    async confirmAndInstall() {
      try {
        this.stage = 'begin';

        // OTA transfer — call runFirmwareUpdate with the already-downloaded manifest
        await this.connection.runFirmwareUpdate(this.manifest, (progress) => {
          this.stage = progress.stage;
          if (progress.stage === 'transferring') {
            this.currentFile = progress.file;
            this.currentFileIndex = progress.fileIndex + 1; // +1 for 1-based display
            this.totalFiles = progress.totalFiles;
          }
        });

        // Post-commit — wait for device to reboot and reconnect
        this.stage = 'rebooting';
        await this.waitForDeviceReconnect();

        this.stage = 'done';
        this.$emit('complete', { version: this.manifest.version });
      } catch (err) {
        this.stage = 'error';
        this.errorMessage = err.message;
        this.$emit('error', { error: err.message });
      }
    },

    async startUpdate() {
      await this.prepareUpdate();
    },

    async waitForDeviceReconnect() {
      // WiFi: the device rebooted onto new firmware and rotated its auth token,
      // so actively re-probe and re-establish the session (poll /auth, adopt
      // the new token, reopen the WebSocket). Throws if it never comes back,
      // which surfaces as the modal's error stage.
      if (typeof this.connection.reconnectAfterReboot === 'function') {
        // Wait until the device is back on the version we just installed — not
        // the old firmware still serving during its post-commit flush window.
        await this.connection.reconnectAfterReboot(this.manifest.version);
        return;
      }
      // USB (or any connection without token rotation): give the device a fixed
      // window to reboot; the transport reconnects on its own.
      await new Promise(resolve => setTimeout(resolve, 5000));
    },

    async retryUpdate() {
      this.isRetrying = true;
      this.manifest = null;
      await this.prepareUpdate();
      this.isRetrying = false;
    },

    closeModal() {
      if (this.isDismissable) {
        this.$emit('close');
      }
    }
  }
};
</script>

<template>
  <dialog ref="dialog" class="modal">
    <div class="modal-box max-w-md">
      <!-- Header -->
      <h3 class="text-lg font-bold">Firmware Update</h3>

      <!-- Content -->
      <div class="py-4">
        <!-- Preparing phase: downloading firmware in background -->
        <div v-if="stage === 'preparing'">
          <div class="flex items-center justify-center mb-4">
            <FontAwesomeIcon icon="spinner" class="animate-spin text-2xl" />
          </div>
          <p class="text-center text-sm text-gray-500">Fetching firmware...</p>
        </div>

        <!-- Confirm phase: show release notes and ask user to confirm -->
        <div v-else-if="stage === 'confirm'">
          <p class="text-sm mb-1">
            Update to firmware <strong>{{ manifest?.version }}</strong>
          </p>
          <div v-if="manifest?.releaseNotes" class="mt-3 mb-4">
            <p class="text-xs font-semibold text-gray-400 mb-1">What's new</p>
            <div class="prose prose-sm prose-invert max-w-none prose-h2:text-xs prose-h2:font-semibold prose-h2:uppercase prose-h2:tracking-wide prose-h2:text-gray-400 text-gray-300 bg-base-200 rounded p-3 max-h-56 overflow-y-auto" v-html="manifest.releaseNotes"></div>
          </div>
          <div v-else class="mt-3 mb-4">
            <p class="text-xs text-gray-400">No release notes available for this version.</p>
          </div>
        </div>

        <!-- Downloading phase -->
        <div v-else-if="stage === 'downloading'">
          <p class="text-sm mb-2">{{ stageLabel }}</p>
          <div class="mb-2">
            <p class="text-xs text-gray-500">{{ currentFile }} ({{ currentFileIndex }}/{{ totalFiles }})</p>
            <progress class="progress w-full" :value="progressPercent" max="100"></progress>
          </div>
          <p class="text-xs text-gray-500 text-center">{{ progressPercent }}%</p>
        </div>

        <!-- Processing phase (begin, ready, transferring, verifying, committing, rebooting) -->
        <div v-else-if="['begin', 'ready', 'transferring', 'verifying', 'verified', 'committing', 'committed', 'rebooting'].includes(stage)">
          <div class="flex items-center justify-center mb-4">
            <FontAwesomeIcon icon="spinner" class="animate-spin text-2xl" />
          </div>
          <p class="text-center text-sm">{{ stageLabel }}</p>
          <div v-if="stage === 'transferring'" class="mt-3">
            <p class="text-xs text-gray-500 text-center">{{ currentFileIndex }}/{{ totalFiles }} files</p>
            <progress class="progress w-full" :value="progressPercent" max="100"></progress>
          </div>
        </div>

        <!-- Success phase -->
        <div v-else-if="stage === 'done'">
          <div class="alert alert-success mb-4">
            <span>✓ Firmware updated successfully!</span>
          </div>
          <p class="text-sm">Device rebooted with new firmware version {{ manifest?.version }}</p>
        </div>

        <!-- Error phase -->
        <div v-else-if="stage === 'error'">
          <div class="alert alert-error mb-4">
            <FontAwesomeIcon icon="circle-exclamation" class="mr-2" />
            <span>Update failed</span>
          </div>
          <p class="text-sm text-red-600">{{ errorMessage }}</p>
          <div v-if="preFlightError" class="mt-2 text-xs bg-red-50 p-2 rounded">
            <p class="font-semibold">Pre-flight error:</p>
            <p>{{ preFlightError }}</p>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="modal-action">
        <form method="dialog">
          <div class="flex gap-2">
            <!-- Confirm stage: Install + Cancel -->
            <template v-if="stage === 'confirm'">
              <button
                class="btn btn-sm btn-primary"
                @click.prevent="confirmAndInstall"
              >
                Install firmware
              </button>
              <button
                class="btn btn-sm btn-ghost"
                @click="closeModal"
              >
                Cancel
              </button>
            </template>

            <!-- Error stage: Retry + Cancel -->
            <template v-else-if="stage === 'error'">
              <button
                class="btn btn-sm btn-primary"
                @click="retryUpdate"
                :disabled="isRetrying"
              >
                <FontAwesomeIcon v-if="isRetrying" icon="spinner" class="animate-spin mr-1" />
                Retry
              </button>
              <button
                class="btn btn-sm btn-ghost"
                @click="closeModal"
              >
                Cancel
              </button>
            </template>

            <!-- Done stage: Close -->
            <template v-else-if="stage === 'done'">
              <button
                class="btn btn-sm btn-ghost"
                @click="closeModal"
              >
                Close
              </button>
            </template>

            <!-- In-progress: no actions, non-dismissable -->
            <template v-else>
              <button class="btn btn-sm btn-ghost" disabled></button>
            </template>
          </div>
        </form>
      </div>
    </div>

    <!-- Backdrop: non-dismissable during transfer -->
    <form v-if="isDismissable" method="dialog" class="modal-backdrop">
      <button @click="closeModal">close</button>
    </form>
    <div v-else class="modal-backdrop" @click.prevent />
  </dialog>
</template>

<style scoped>
.modal-box {
  position: relative;
}

/* Make modal non-dismissable by clicking outside during transfer */
.modal-backdrop {
  cursor: not-allowed;
}
</style>
